import { Button, IconButton, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import Iconify from "../../../minimals/components/iconify";
import { formatUTCDateWithLocalTime } from "../../../utils";
import BulletinRowData from "../BulletinRowData";
import { createRemovalRequestColumn } from "../RemovalRequest/RemovalRequestColumn";
import { BlacklistEntityType, PropertyRecord, RemovalRequest } from "../types";

export const propertyRecordColumns = (refreshData: () => Promise<void>, onEdit?: (entityId: number) => void): MRT_ColumnDef<PropertyRecord>[] => [
  ...(onEdit ? [{
    id: "edit",
    header: "",
    size: 80,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }: any) => (
      <IconButton onClick={() => onEdit(row.original.id)}>
       <Iconify icon={"eva:edit-fill"} />
    </IconButton>
    ),
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } }
  }] as MRT_ColumnDef<PropertyRecord>[] : []),
  {
    accessorKey: "id",
    header: "ID",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  createRemovalRequestColumn<PropertyRecord>(refreshData, BlacklistEntityType.Property),
  {
    accessorKey: "createdByUser.fullName",
    header: "Created By",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "createdDate",
    header: "Created Date",
    size: 50,
    accessorFn: (row) => (row.createdDate ? formatUTCDateWithLocalTime(row.createdDate) : null),
    Cell: ({ renderedCellValue }) => <Typography variant="subtitle2">{renderedCellValue}</Typography>,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "address",
    header: "Address",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "unit",
    header: "Unit",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "city",
    header: "City",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "zip",
    header: "Zip",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } }
  },
  {
    accessorKey: "county.name",
    header: "County",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "section",
    header: "Section",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "block",
    header: "Block",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "lots",
    header: "Lots",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" },
    Cell: ({ row }) => {
      const lots = row?.original?.lots;
      if (!lots.length) return <></>;
      return (
        <div style={{ maxHeight: "150px", overflowY: "auto" }}>{lots.map((l) => `Lot ${l.lotNumber}`).join(", ")}</div>
      );
    }
  },
  {
    accessorKey: "parcels",
    header: "Parcel Ids",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" },
    Cell: ({ row }) => {
      const parcels = row?.original?.parcels;
      if (!parcels.length) return <></>;
      return (
        <div style={{ maxHeight: "150px", overflowY: "auto" }}>
          {parcels.map((p) => `Parcel ${p.parcelId}`).join(", ")}
        </div>
      );
    }
  },
  {
    accessorKey: "subdivision",
    header: "Subdivision",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "bulletins",
    header: "Bulletins",
    size: 900,
    Cell: ({ row }) => {
      const bulletins = row.original.bulletins;
      if (!bulletins || bulletins.length === 0) return <span>No bulletins</span>;
      return (
        <div style={{ maxHeight: "150px", overflowY: "auto" }}>
          {bulletins.map((bulletinRecord, index) => (
            <BulletinRowData key={index} bulletin={bulletinRecord.bulletin} />
          ))}
        </div>
      );
    },
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "latitude",
    header: "Latitude",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "longitude",
    header: "Longitude",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "geocodedAddress",
    header: "Geocoded Address",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  },
  {
    accessorKey: "notes",
    header: "Notes",
    size: 50,
    muiTableBodyCellProps: { sx: { verticalAlign: "top" } },
    muiFilterTextFieldProps: { variant: "outlined", size: "small" }
  }
];
